import { cn } from "@base/lib/utils";
import { DAILY_LOG_STEPS, STEP_TITLES } from "@features/write-daily-log/model/constants";
import type { DailyLogRailStepState } from "@features/write-daily-log/model/dailyLogStepNavigation";
import { Check } from "lucide-react";

interface DailyLogStepRailProps {
  stepStates: DailyLogRailStepState[];
  currentIndex: number;
  onSelectStep: (targetIndex: number) => void;
  /** draft를 폼에 불러오는 중처럼 단계 이동을 막아야 할 때 */
  disabled?: boolean;
}

/**
 * 1024px 이상에서만 보이는 작성/수정 단계 rail.
 * 이동 가능 여부는 방문 여부가 아니라 앞 단계들의 입력 유효성으로 정한다.
 */
export function DailyLogStepRail({ stepStates, currentIndex, onSelectStep, disabled = false }: DailyLogStepRailProps) {
  return (
    <nav
      aria-label="Daily log steps"
      className="hidden lg:sticky lg:top-[calc(var(--write-header-height,0px)+16px)] lg:flex lg:flex-col lg:gap-1 lg:w-[220px] lg:shrink-0"
    >
      {DAILY_LOG_STEPS.map((step, index) => {
        const isCurrent = index === currentIndex;
        const isCompleted = stepStates[index]?.isCompleted ?? false;
        const reachable = !disabled && (stepStates[index]?.isReachable ?? isCurrent);

        return (
          <button
            key={step}
            type="button"
            aria-current={isCurrent ? "step" : undefined}
            disabled={!reachable}
            onClick={() => onSelectStep(index)}
            className={cn(
              "flex gap-3 items-center px-3 py-2.5 rounded-8 text-left text-body3 transition-colors",
              isCurrent ? "bg-blue-10 text-blue-600" : "text-gray-900",
              reachable && !isCurrent && "hover:bg-gray-100",
              !reachable && "opacity-50 cursor-not-allowed",
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "flex justify-center items-center shrink-0 size-6 rounded-circle text-label3",
                isCurrent && "bg-blue-500 text-white",
                isCompleted && "bg-blue-100 text-blue-600",
                !isCurrent && !isCompleted && "bg-gray-100 text-gray-500",
              )}
            >
              {isCompleted ? <Check width={14} height={14} /> : index + 1}
            </span>
            {STEP_TITLES[step]}
          </button>
        );
      })}
    </nav>
  );
}
